<template>
  <v-dialog v-model="certificatesCreateModal" persistent width="500">
    <v-card tile>
      <v-card-title class="headline">{{ $t('certificatesCreate') }}</v-card-title>
      <v-card-text>
          <v-select
            :items="locationRates"
            :label="$t('certificatesRate') + ' *'"
            v-model="certificate.rate"
            required
            :error-messages="rateErrors"
            @blur="$v.certificate.rate.$touch()"
            item-text="title"
            item-value="value"
        >
          <template v-slot:item="data">
            <div v-html="data.item.title"></div>
          </template>
          <template v-slot:selection="data">
            <div v-html="data.item.title"></div>
          </template>
        </v-select>
        <v-select
            :items="typeOptions"
            :label="$t('paymentsType') + ' *'"
            v-model="certificate.type"
            required
            :error-messages="typeErrors"
            @blur="$v.certificate.type.$touch()"
            item-text="title"
            item-value="value"
        ></v-select>
        <v-select
            v-if="certificateDesigns.length"
            :items="locationDesigns"
            :label="$t('certificatesDesign') + ' *'"
            v-model="certificate.design"
            required
            :error-messages="designErrors"
            @blur="$v.certificate.design.$touch()"
            item-text="name"
            item-value="name"
        ></v-select>
        <v-text-field
            v-else
            :label="$t('certificatesDesign') + ' *'"
            v-model="certificate.design"
            required
            :error-messages="designErrors"
            @input="$v.certificate.design.$touch()"
            @blur="$v.certificate.design.$touch()"
        ></v-text-field>
        <v-text-field
          :label="$t('certificatesQuantity') + ' *'"
          required
          v-model="certificate.quantity"
          type="number"
          class="mt-4"
          :error-messages="quantityErrors"
          @input="$v.certificate.quantity.$touch()"
          @blur="$v.certificate.quantity.$touch()"
        ></v-text-field>
        <small>* {{ $t("validation") }}</small>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="red darken-1" text @click="closeForm()">{{
          $t("btnExit")
        }}</v-btn>
        <v-btn
          color="green darken-1"
          text
          @click="submitForm()"
          :disabled="$v.certificate.$invalid"
          >{{ $t("btnCreate") }}</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { validationMixin } from "vuelidate";
import { required, minValue, maxValue, maxLength } from "vuelidate/lib/validators";

export default {
  name: "certificatesCreateModal",

  mixins: [validationMixin],

  props: ["certificatesCreateModal"],

  data() {
    return {
      locationRates: [],
      locationDesigns: [],
      certificate: {
        rate: null,
        type: null,
        design: null,
        quantity: 1,
        location_id: ''
      },
      typeOptions: [
        { title: this.$i18n.t("certificatesTypeDigital"), value: 'digital' },
        { title: this.$i18n.t("certificatesTypePhysical"), value: 'physical' },
      ],
    };
  },

  validations: {
    certificate: {
      rate: {
        required,
      },
      type: {
        required,
      },
      design: {
        required,
        maxLength: maxLength(15)
      },
      quantity: {
        required,
        minValue: minValue(1),
        maxValue: maxValue(100),
      },
    },
  },
  computed: {
    ...mapGetters(["location", "certificateRates", "certificateDesigns", "language"]),
    rateErrors() {
      const errors = [];
      if (!this.$v.certificate.rate.$dirty) return errors;
      !this.$v.certificate.rate.required &&
      errors.push(this.$i18n.t("validationRequired"));
      return errors;
    },
    designErrors() {
      const errors = [];
      if (!this.$v.certificate.design.$dirty) return errors;
      !this.$v.certificate.design.required &&
      errors.push(this.$i18n.t("validationRequired"));
      !this.$v.certificate.design.maxLength &&
      errors.push(this.$i18n.t("validationCertificateLength"));
      return errors;
    },
    typeErrors() {
      const errors = [];
      if (!this.$v.certificate.type.$dirty) return errors;
      !this.$v.certificate.type.required &&
      errors.push(this.$i18n.t("validationRequired"));
      return errors;
    },
    quantityErrors() {
      const errors = [];
      if (!this.$v.certificate.quantity.$dirty) return errors;
      !this.$v.certificate.quantity.required &&
      errors.push(this.$i18n.t("validationRequired"));
      !this.$v.certificate.quantity.minValue &&
      errors.push(this.$i18n.t("validationMinValue"));
      !this.$v.certificate.quantity.maxValue &&
      errors.push(this.$i18n.t("validationMaxValue"));
      return errors;
    },
  },

  methods: {
    ...mapActions(["loadCertificateRates", "loadCertificateDesigns", "certificateCreating"]),
    formatCurrencyAmount(amount, locale) {
      return parseFloat(amount).toLocaleString(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    },
    formatCurrency(amount, currency) {
      const { code, is_before, blank_place } = currency;
      return is_before
          ? `${code}${blank_place}${amount}`
          : `${amount}${blank_place}${code}`;
    },
    formatLocationRates() {
      if (this.certificateRates) {
        this.locationRates = this.certificateRates.map(rate => {
          const formattedRate = this.formatCurrencyAmount(rate.rate, this.language);
          const title = this.formatCurrency(formattedRate, this.location.b2b_currency);

          return {
            title,
            value: rate.rate
          };
        });
      }
    },
    formatLocationDesigns() {
      if (this.certificateDesigns) {
        this.locationDesigns = this.certificateDesigns.map(cert => {
          return {
            name: cert.name
          }
        })
      }
    },
    submitForm() {
      this.certificate.location_id = this.location.id
      this.certificateCreating(this.certificate)
          .then(() => {
        this.clearCertificate();
        this.$emit("reload-certificates");
        this.$emit("close-dialog");
      })
    },
    closeForm() {
      this.clearCertificate();
      this.$emit("close-dialog");
    },
    clearCertificate() {
      this.certificate = {
        rate: null,
        type: null,
        design: null,
        quantity: 1,
      };
    },
  },

  watch: {
    async certificatesCreateModal(visible) {
      if (!visible) {
        this.$v.$reset();
      } else {
        if (!this.locationRates.length) {
          await this.loadCertificateRates();
          this.formatLocationRates();
        }
        if (!this.locationDesigns.length) {
          await this.loadCertificateDesigns();
          this.formatLocationDesigns();
        }
      }
    },
  },
};
</script>
